import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import {
    writeFieldToForm,
    ClassicFieldMeta,
    classicFieldErrorMessage,
    formatShortFieldGroupDate,
    sameFieldGroupDate,
    toDisplayDate,
} from "./formFieldHelpers";
import DateField from "../../../common/formFields/DateField";
import { DEFAULT_MIN_DATE, DEFAULT_MAX_DATE } from "../../../common/constants/ApiConstants";

export interface DueDateFieldProps {
    /** Currently selected Due date (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: Date | string;
    /** Hides the field entirely - Classic's `requestType != DISCLOSURE_ACCOUNTING` gate. */
    readonly visible: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /** Extra, non-form side effect on change (classic pane's autosave PATCH). The DATA_ENTRY_FIELDS_FORM write is always done here. */
    readonly onDueDateChange?: (newValue?: Date | string) => void;
    /** AB#38460: unusable text to keep on screen, and the channel that reports it. Forwarded verbatim to DateField - see its own docblocks. */
    readonly invalidText?: string;
    readonly onInvalidText?: (text?: string, committed?: boolean) => void;
    /** AB#38460: forwarded verbatim to DateField - see its docblock. */
    readonly resyncKey?: string | number;
    /** Real redux-form validation state, threaded down by Classic Mode; omitted by Quick Entry. */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Bug fix (AB#35144): Quick Entry had no Due Date field, so a value Classic Mode exposes (and the
 * AI already extracts) couldn't be seen or set in Quick Entry.
 *
 * Mirrors Classic's own Due Date field: label "Due date", mm/dd/yyyy placeholder, DEFAULT_MIN_DATE /
 * DEFAULT_MAX_DATE bounds (a due date is legitimately in the FUTURE, so - unlike Received Date - it
 * must NOT be capped at today), and hidden for DISCLOSURE_ACCOUNTING request types via `visible`.
 * Not required in Classic, so the row never blocks Confirm All.
 */
const DueDateField: React.FC<DueDateFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (value?: Date | string) => {
        writeFieldToForm(props.dispatch, FormConstants.DUE_DATE, value, formName);
        props.onDueDateChange?.(value);
    };

    return (
        <DateField
            label="Due date"
            placeholder="mm/dd/yyyy"
            minDate={DEFAULT_MIN_DATE}
            maxDate={DEFAULT_MAX_DATE}
            errMsg={classicFieldErrorMessage(props.meta)}
            invalidText={props.invalidText}
            onInvalidText={props.onInvalidText}
            resyncKey={props.resyncKey}
            onChange={onChange}
            // AB#37843: display-only normalization - see toDisplayDate. The write
            // path (onChange above) is untouched; only what the picker SHOWS changes.
            value={toDisplayDate(props.value)}
            name={FormConstants.DUE_DATE}
        />
    );
};

export default DueDateField;

export const DUE_DATE_ROW_ID = "dueDate";

/**
 * Builds the FieldGroupRow for the Due Date row. Due Date is NOT required in Classic, so an empty
 * value is EmptyNotRequired (never NeedsEntry) and therefore never blocks Confirm All.
 *
 * `aiValue` is the AI-extracted due date (`requestAnalysis.data.dueDate`); DataEntryDetails seeds it
 * into the form field, so the row shows it and reads as AI-pre-selected until the specialist edits it.
 */
export function mapDueDateToFieldGroupRow(
    badgeNumber: number,
    formValue: Date | string | undefined,
    aiValue?: Date | string,
): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null && formValue !== "";

    return {
        id: DUE_DATE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.EmptyNotRequired,
        label: "Due Date",
        value: hasValue ? formatShortFieldGroupDate(formValue!) : null,
        isAiPreSelected: Boolean(aiValue) && sameFieldGroupDate(formValue, aiValue),
    };
}
